import { useEffect, useMemo, useState } from "preact/hooks";
import { fetchChatConfig, sendMessage, sendQuickReply, submitLead } from "./lib/api";
import { getVisitorId } from "./lib/visitor";
import type { ChatMessage, ConfigResponse, LeadPayload, WidgetConfig, WidgetPosition } from "./lib/types";
import ChatBubble from "./components/ChatBubble";
import ChatWindow from "./components/ChatWindow";

type AppProps = {
  config: WidgetConfig;
};

const ERROR_MESSAGE = "Le chat est momentanement indisponible.";

function createMessage(role: ChatMessage["role"], content: string, source?: ChatMessage["source"]): ChatMessage {
  return {
    id: `${role}_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    role,
    content,
    source
  };
}

export default function App({ config }: AppProps) {
  const visitorId = useMemo(() => getVisitorId(), []);
  const [remoteConfig, setRemoteConfig] = useState<ConfigResponse | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [leadCaptureVisible, setLeadCaptureVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadConfig() {
      try {
        const loadedConfig = await fetchChatConfig(config);
        if (cancelled) {
          return;
        }

        setRemoteConfig(loadedConfig);
        setMessages([createMessage("assistant", loadedConfig.chatbot.welcome_message)]);
      } catch (loadError) {
        if (cancelled) {
          return;
        }

        if (config.debug) {
          console.error("Quiky chatbot config failed", loadError);
          setError(ERROR_MESSAGE);
        } else {
          setDisabled(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadConfig();

    return () => {
      cancelled = true;
    };
  }, [config]);

  if (disabled) {
    return null;
  }

  const primaryColor = config.primaryColorOverride ?? remoteConfig?.chatbot.primary_color ?? config.primaryColor;
  const position: WidgetPosition = config.positionOverride ?? remoteConfig?.chatbot.position ?? config.position;
  const chatbotId = remoteConfig?.chatbot.id ?? "";
  const conversationStarted = Boolean(conversationId) || messages.some((message) => message.role === "user");

  async function handleQuickReply(quickReplyId: string, label: string) {
    if (!remoteConfig || sending) {
      return;
    }

    setSending(true);
    setError(null);
    setMessages((current) => [...current, createMessage("user", label, "quick_reply")]);

    try {
      const response = await sendQuickReply({
        config,
        chatbotId,
        visitorId,
        conversationId,
        quickReplyId
      });

      setConversationId(response.conversation_id);
      setMessages((current) => [...current, createMessage("assistant", response.reply, response.source)]);
      setLeadCaptureVisible(response.lead_capture_suggested);
    } catch (replyError) {
      if (config.debug) {
        console.error("Quiky quick reply failed", replyError);
      }
      setError(ERROR_MESSAGE);
      setMessages((current) => [...current, createMessage("assistant", remoteConfig.chatbot.fallback_message, "fallback")]);
      setLeadCaptureVisible(true);
    } finally {
      setSending(false);
    }
  }

  async function handleSendMessage(message: string) {
    if (!remoteConfig || sending) {
      return;
    }

    setSending(true);
    setError(null);
    setMessages((current) => [...current, createMessage("user", message)]);

    try {
      const response = await sendMessage({
        config,
        chatbotId,
        visitorId,
        conversationId,
        message
      });

      setConversationId(response.conversation_id);
      setMessages((current) => [...current, createMessage("assistant", response.reply, response.source)]);
      setLeadCaptureVisible(response.lead_capture_suggested);
    } catch (messageError) {
      if (config.debug) {
        console.error("Quiky message failed", messageError);
      }
      setError(ERROR_MESSAGE);
      setMessages((current) => [...current, createMessage("assistant", remoteConfig.chatbot.fallback_message, "fallback")]);
      setLeadCaptureVisible(true);
    } finally {
      setSending(false);
    }
  }

  async function handleLeadSubmit(lead: LeadPayload) {
    if (!remoteConfig || !conversationId || sending) {
      return;
    }

    setSending(true);
    setError(null);

    try {
      const response = await submitLead({
        config,
        chatbotId,
        conversationId,
        lead
      });

      setLeadCaptureVisible(false);
      setMessages((current) => [...current, createMessage("assistant", response.message, "lead_capture")]);
    } catch (leadError) {
      if (config.debug) {
        console.error("Quiky lead submit failed", leadError);
      }
      setError("Impossible d'envoyer vos coordonnees pour le moment.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className={`qk-chatbot qk-chatbot--${position}`} style={{ "--qk-primary": primaryColor }}>
      {opened && (
        <ChatWindow
          error={error}
          loading={loading}
          ready={Boolean(remoteConfig)}
          sending={sending}
          messages={messages}
          quickReplies={conversationStarted ? [] : remoteConfig?.quick_replies ?? []}
          leadCaptureVisible={leadCaptureVisible}
          canSubmitLead={Boolean(conversationId)}
          onClose={() => setOpened(false)}
          onQuickReply={handleQuickReply}
          onSendMessage={handleSendMessage}
          onSubmitLead={handleLeadSubmit}
          onDismissLeadCapture={() => setLeadCaptureVisible(false)}
        />
      )}
      <ChatBubble loading={loading} opened={opened} onClick={() => setOpened((value) => !value)} />
    </div>
  );
}
